<script>
  import { createEventDispatcher, onDestroy, onMount } from 'svelte';
  import { ignore, makeWebsiteRequest } from '../Common/tools';
  import Table from '../Table/Table.svelte';
  import Order from './Order.svelte';
  import UnarchiveStatusPicker from './UnarchiveStatusPicker.svelte';
  import { AppCache } from '../stores';
  import { External, M } from '../external';
  import NoDataPlaceholder from '../Common/NoDataPlaceholder.svelte';

  export let period = null,
    funnel = null,
    filters = [],
    showArchived = false,
    contact = 0,
    search = '',
    options = {};

  let shouldSaveDataWhenLeaving = false;

  const dispatch = createEventDispatcher();

  let loading = true,
    initialLoad = true,
    currentPage = 0,
    pagesCount = 0,
    itemsCount = 0,
    table = null,
    selection = null;

  let tableColumns = [
    { key: 'no', label: M.INVOICE_NO, class: 'flex-none mw-120' },
    ...(options.name === false
      ? []
      : [
        {
          key: 'customerName',
          label: M.CUSTOMER_NAME,
          class: 'flex-none mw-160',
          row: 'modified',
          style: 'font-size: 13px',
          rowLabel: function(order, column) {
            return order.customerEmail;
          }
        }
      ]),
    {
      key: 'totalLbl',
      label: M.TOTAL,
      class: 'flex-none',
      style: 'font-size: 15px',
      suffix: (order, column) => {
        return order.isSubscriptionRenewal
          ? '<span class="material-icons-outlined repeating-order-badge button-tooltip" data-tooltip="subscriptionRenewalOrder">history</span>'
          : '';
      }
    },
    {
      key: 'paidLbl',
      label: M.PAID,
      rowClass: function(order, column) {
        return 'pay-status' + (order.paid ? ' paid' : ' not-paid');
      },
      class: 'flex-none mw-100',
      onClick: (order, column, target) => {
        onPaymentStatusClicked(order, target);
      }
    },
    // include the status column if we're not showing Archived orders. I.e. auto-expand an array with the column
    ...(showArchived
      ? []
      : [
        {
          key: 'statusLbl',
          label: M.STATUS,
          rowClass: function(order, column) {
            return 'order-status ' + order.status.toLowerCase();
          },
          onClick: (order, column, target) => {
            onOrderStatusClicked(order, target);
          },
          class: 'flex-none mw-120'
        }
      ]),
    {
      key: 'createdDate',
      label: M.CREATED,
      class: 'flex-none',
      row: 'modified',
      style: 'font-size: 13px',
      rowLabel: function(row, column) {
        return row.createdTime;
      }
    },
    {
      key: 'paymentType',
      label: M.PAYMENT,
      class: 'flex-none mw-120'
    }
  ];

  let tableData = {
    columns: tableColumns,
    rows: []
  };

  let fillItemDetails = (item) => {
    item.paidLbl = item.paid ? M.PAID : M.NOT_PAID;
    switch (item.paymentType) {
      case 'cod':
        item.paymentType = M.PAYMENT_COD;
        break;
      case 'bank':
        item.paymentType = M.PAYMENT_BANK;
        break;
      case 'stripe':
      case 'stripeConnect':
      case 'stripe-connect':
        item.paymentType = M.PAYMENT_STRIPE;
        break;
      case 'braintree':
        item.paymentType = M.PAYMENT_BRAINTREE;
        break;
      case '2checkout':
        item.paymentType = M.PAYMENT_2CHECKOUT;
        break;
      case 'paypal':
        item.paymentType = M.PAYMENT_PAYPAL;
        break;
      case 'no-payment':
        item.paymentType = '-';
        break;
      default:
        item.paymentType = item.paymentType.charAt(0).toUpperCase() + item.paymentType.slice(1);
        break;
    }
    item.statusLbl = M['ORDER_STATUS_' + item.status] || item.status;
    item.totalLbl = External.WebPlatform.currToStr(item.total, External.Website.storeCurrencySymbol, External.Website.storeCurrencyPositionAtLeft, External.Website.storeCurrency, External.Website.storeCountry);
    let orderDate = External.WebsiteSettings.getDateFormat() === 'uk' ? External.WebPlatform.ukDateTimeStringToDate(item.orderTime) : External.WebPlatform.usDateTimeStringToDate(item.orderTime);
    item.createdDate = External.WebPlatform.dateToStrWithMonth(orderDate, false, true);
    item.createdTime = item.orderTime.substr(item.orderTime.indexOf(' ') + 1);
  };

  let setRequestFilter = (request, stringify) => {
    request = request || {};

    request.contact = contact;

    if (filters.length > 0) {
      request.filters = stringify ? JSON.stringify(filters) : filters;
    }

    if (funnel) {
      request.funnel = funnel;
    }
    if (period && period.from) {
      request.from = External.Tools.dateToISOStr(period.from);
    }

    if (period && period.to) {
      request.to = External.Tools.dateToISOStr(period.to);
    }

    request.search = search;
    request.archived = showArchived;
    return request;
  };

  let load = async (page) => {
    if (loading && !initialLoad) {
      return;
    }
    initialLoad = false;

    if (page === 0) {
      tableData.rows = [];
      selection = null;
    } else if (currentPage + 1 >= pagesCount) {
      return;
    }
    loading = true;

    let request = setRequestFilter(
      {
        page: page
      },
      true
    );

    let response = await makeWebsiteRequest({
      action: 'getStoreOrders',
      method: 'post',
      data: request
    });

    loading = false;
    if (!response.data) return;

    currentPage = page;
    pagesCount = response.data.pagesCount;
    itemsCount = response.data.count;

    response.data.items.forEach((item) => {
      fillItemDetails(item);
    });

    if (page === 0) {
      notifyItemsLoaded();
    }
    if (table) table.addRows(response.data.items);
  };

  let notifyItemsLoaded = () => {
    dispatch('itemsloaded', {
      count: itemsCount,
      label: showArchived ? M.ARCHIVED : filters.length ? '' : M.ALL_ORDERS
    });
  };

  let onNext = () => {
    load(currentPage + 1);
  };

  let onClickRow = (row) => {
    if (options.click === false) {
      return;
    }

    if (options.openOrdersIn === 'popup') {
      External.Tools.showWindow({
        isModal: false,
        withDarkBackground: true,
        noNav: true,
        noTitle: true,
        width: 1200,
        forceAtRight: true, //External.showBuilderPopupsOnFullHeight,
        action: {
          onShow: (popup, ui) => {
            popup.bindComponent(
              new Order({
                target: popup.getContentArea(),
                props: {
                  id: row.id,
                  options: {
                    inPopup: true
                  }
                }
              })
            );
          },
          onBeforeHide: (popup, ui, closeAction) => {
            if (popup.component().isChanged()) {
              External.CPanel.showUnsavedChangesPrompt(() => {
                closeAction();
              }, ignore);
              return false;
            }
          }
        }
      });
    } else {
      shouldSaveDataWhenLeaving = true;
      External.CPanel.goToUrl('/me/store/orders/' + row.id);
    }
  };

  let onPaymentStatusClicked = (row, clickedDomElement) => {
    const items = [{ text: row.paid ? M.MARK_NOT_PAID : M.MARK_PAID, icon: 'fa-money', tag: 'paid' }];

    External.Tools.showTooltipOptions(clickedDomElement, items, async function(target, idx, text, tag) {
      let result = await makeWebsiteRequest({
        action: 'changeOrderPaymentStatus',
        data: {
          id: row.id,
          paid: !row.paid
        }
      });

      let order = result.data;

      let hasDigitalGoods = order.items.filter((item) => item.type === 'DIGITAL' || item.type === 'MEMBERSHIP' || item.type === 'PAYWALL').length > 0;

      row.paid = !row.paid;
      row.paidLbl = row.paid ? M.PAID : M.NOT_PAID;
      table.refreshRowById(row.id);

      if (row.paid && hasDigitalGoods) {
        External.Tools.prompt(
          M.SEND_DIGITAL_GOODS_EMAIL,
          M.SEND_DIGITAL_GOODS_EMAIL_PROMPT,
          async () => {
            let res = await makeWebsiteRequest({
              action: 'sendDigitalGoodsEmails',
              data: {
                id: row.id
              }
            });

            External.Tools.showAutohideMessage(1000, M.EMAIL_SENT, 400);
          },
          ignore,
          M.YES,
          M.NO
        );
      }
    });
  };

  let onOrderStatusClicked = (row, clickedDomElement) => {
    let options = [];

    if (row.status !== 'PENDING') {
      options.push({ text: M.MARK_PENDING, icon: 'fa-circle-o', tag: 'PENDING' });
    }

    if (row.hasShipping && row.status !== 'SHIPPED') {
      options.push({ text: M.MARK_AS_SHIPPED, icon: 'fa-truck', tag: 'SHIPPED' });
    }

    if (row.status !== 'COMPLETED') {
      options.push({ text: M.MARK_AS_COMPLETED, icon: 'fa-check', tag: 'COMPLETED' });
    }

    if (row.status !== 'CANCELED') {
      options.push({ text: M.MARK_AS_CANCELED, icon: 'fa-ban', tag: 'CANCELED' });
    }

    External.Tools.showTooltipOptions(clickedDomElement, options, function(target, idx, text, status) {
      let askForEmail = () => {
        External.Tools.prompt(
          '',
          M.ORDER_NOTIFY_MSG,
          () => {
            setStatus(true);
          },
          () => {
            setStatus(false);
          }
        );
      };

      let setStatus = async (notify) => {
        let result = await makeWebsiteRequest({
          action: 'changeOrderStatus',
          data: {
            id: row.id,
            status: status,
            notify: notify ? '1' : '0'
          }
        });

        row.status = status;
        row.statusLbl = M['ORDER_STATUS_' + status] || row.status;
        table.refreshRowById(row.id);
      };

      if (status === 'CANCELED') {
        External.Tools.prompt(M.DEL_CONFIRM, M.CANCEL_ORDER_CONFIRM, askForEmail);
      } else {
        askForEmail();
      }
    });
  };

  export function reload() {
    load(0);
  }

  function getSelectedItemsCount() {
    if (selection !== null) {
      if (selection.all) return itemsCount - selection.unselected.length;
      else return selection.selected.length;
    }
    return itemsCount;
  }

  export function archive() {
    let itemsToExport = getSelectedItemsCount();
    External.Tools.prompt(
      M.ARCHIVE_ORDERS,
      External.format(M.ARCHIVE_ORDERS_CONFIRM, 'count', itemsToExport),
      async function() {
        let request = setRequestFilter(
          {
            selection: JSON.stringify(selection)
          },
          true
        );

        let response = await makeWebsiteRequest({
          action: 'setOrdersStatus',
          method: 'post',
          data: request
        });

        await load(0);
      },
      ignore,
      M.YES,
      M.NO
    );
  }

  export function unarchive() {
    External.Tools.showWindow({
      title: M.UNARCHIVE_ORDERS,
      width: 450,
      isModal: true,
      withDarkBackground: true,
      showCloseButton: true,
      action: {
        onShow: function(popup) {
          popup.bindComponent(
            new UnarchiveStatusPicker({
              target: popup.getContentArea(),
              props: {
                onSelect: function(status) {
                  External.Tools.prompt(
                    M.UNARCHIVE_ORDERS,
                    External.format(M.UNARCHIVE_ARE_YOU_SURE, 'x', getSelectedItemsCount()),
                    async function() {
                      popup.close();
                      let request = setRequestFilter({
                          selection: JSON.stringify(selection),
                          status: status
                        },
                        true
                      );
                      await makeWebsiteRequest({
                        action: 'setOrdersStatus',
                        method: 'post',
                        data: request
                      });
                      await load(0);
                    },
                    ignore,
                    M.YES,
                    M.NO
                  );
                }
              }
            })
          );
        }
      }
    });
  }

  export function startExport() {
    let itemsToExport = getSelectedItemsCount();
    External.Tools.prompt(
      M.EXPORT_ORDERS,
      External.format(M.EXPORT_ORDERS_CONFIRM, 'count', itemsToExport),
      function() {
        let params = setRequestFilter(
          {
            selection: selection
          },
          false
        );
        External.CPanel.initiateExport('EXPORT_STORE_ORDERS', params, M.ORDERS_EXPORT_IN_PROGRESS);
      },
      ignore,
      M.YES,
      M.NO
    );
  }

  export function startFeesExport() {
    let itemsToExport = getSelectedItemsCount();
    External.Tools.prompt(
      M.EXPORT_PLATFORM_FEES || 'Export Platform Fees',
      External.format(M.EXPORT_ORDERS_CONFIRM, 'count', itemsToExport),
      function() {
        let params = setRequestFilter(
          {
            selection: selection
          },
          false
        );
        External.CPanel.initiateExport('EXPORT_STORE_ORDER_FEES', params, M.ORDERS_EXPORT_IN_PROGRESS);
      },
      ignore,
      M.YES,
      M.NO
    );
  }

  export function shouldSaveDataOnExit() {
    return shouldSaveDataWhenLeaving;
  }

  export function saveDataOnExit() {
    if (!shouldSaveDataWhenLeaving) {
      return;
    }

    AppCache.update((cache) => {
      let data = cache.ordersTable || {};
      cache.ordersTable = data;
      data.currentPage = currentPage;
      data.pagesCount = pagesCount;
      data.itemsCount = itemsCount;
      data.tableData = tableData;
      return cache;
    });
  }

  onMount(async () => {
    if ($AppCache.ordersTable && contact === 0) {
      // restore saved details so we can continue from where we were when we navigated away
      let cached = $AppCache.ordersTable;
      currentPage = cached.currentPage;
      pagesCount = cached.pagesCount;
      itemsCount = cached.itemsCount;
      tableData = cached.tableData;
      loading = false;
      initialLoad = false;

      if ($AppCache.lastOrder) {
        let order = $AppCache.lastOrder;
        for (let idx = 0; idx < tableData.rows.length; ++idx) {
          let current = tableData.rows[idx];
          if (current.id === order.id) {
            current.status = order.status;
            current.total = order.total;
            current.paid = order.paid;
            current.customerEmail = order.customerEmail;
            current.customerName = order.customerName;
            fillItemDetails(current);
            break;
          }
        }
        setTimeout(() => {
          table.scrollIntoView(parseInt(order.id));
        }, 10);
      }

      AppCache.update((cache) => {
        cache.ordersTable = null;
        return cache;
      });
      notifyItemsLoaded();
    } else {
      await load(0);
    }
  });
</script>

<Table
  bind:this={table}
  activeColumns={tableData.columns}
  data={tableData.rows}
  loading={loading}
  hideHeadOnLoad={true}
  hasSelection={options.selection !== false}
  bind:selection
  onClick={onClickRow}
  nextPage={onNext}
  tableClass={"orders-scrollable-table"}>

  <NoDataPlaceholder message={M.NO_DATA_YET} />

</Table>
